import type { BasketModel } from '@accesso/cc-ui/model';
import type { EffectRef } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, GuardResult, RouterStateSnapshot } from '@angular/router';
import { isPlatformServer, PlatformLocation } from '@angular/common';
import { effect, inject, Injector, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { isNonEmptyString } from '@accesso/cc-std-lib';

import { CartStore } from '../../feature/cart/cart.store';
import { MerchantStore } from '../../feature/merchant/merchant.store';
import { StorageService } from '../../feature/storage/storage.token';
import { QUERY_PARAM_AUTH_REDIRECT } from '../enums/query-params';
import { AuthService } from './auth.service';

export const profileCompleteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
	if (isPlatformServer(inject(PLATFORM_ID))) return true;

	const storage = inject(StorageService);
	const cart = inject(CartStore);
	const location = inject(PlatformLocation);
	const router = inject(Router);
	const auth = inject(AuthService);
	const injector = inject(Injector);
	const merchantStore = inject(MerchantStore);

	return auth.initialize().then(() => {
		const url = state.url ?? location.pathname + location.search;
		const redirectTree = router.createUrlTree(['/auth_callback'], { queryParams: { [QUERY_PARAM_AUTH_REDIRECT]: url } });

		return new Promise<GuardResult>((resolve) => {
			let effectRef: EffectRef | null = null;

			const resolveGuard = (result: GuardResult) => {
				effectRef?.destroy();
				resolve(result);
			};

			effectRef = effect(
				() => {
					const profile = auth.profile();
					const basket = cart.basket();
					const cartId = cart.cartId();

					// Wait while the cart is initializing or loading
					if (cartId && (!basket || basket.state().loading)) return;

					// Short-circuit: profile completeness is irrelevant in these cases
					const bypassed =
						!storage.isSupported ||
						state.url?.startsWith('/auth_callback') ||
						location.pathname?.startsWith('/auth_callback') ||
						!merchantStore.merchant()?.usesProfiles();

					if (bypassed || !profile) {
						if (!profile && basket) removeAssignedItems(basket);
						return resolveGuard(true);
					}

					const profileState = profile.state();
					if (profileState.loading) return;
					if (profileState.error) return resolveGuard(redirectTree);

					// Profile is complete — allow through without modifying the basket
					if (profileState.loaded) {
						const { firstName, lastName } = profile.personal() ?? {};
						if ([firstName, lastName].every(isNonEmptyString)) return resolveGuard(true);
					}

					// Profile is authenticated but incomplete — clean stale items and redirect
					const allowedIds = [
						profile.id,
						...(profile
							.cohort()
							?.map(({ externalId }) => externalId)
							.filter(isNonEmptyString) ?? []),
					];
					if (basket) removeNonMatchingItems(basket, allowedIds);
					return resolveGuard(redirectTree);
				},
				{ injector, manualCleanup: true }
			);
		});
	});
};

function removeAssignedItems(basket: BasketModel | null): void {
	if (!basket) return;
	const ids = basket
		.items()
		.filter((item) => isNonEmptyString(item.person?.externalId) || isNonEmptyString(item.person?.auid))
		.map(({ id }) => id);
	if (ids.length) basket.removeItems(ids);
}

function removeNonMatchingItems(basket: BasketModel | null, allowedIds: string[]): void {
	if (!basket) return;
	const ids = basket
		.items()
		.filter(({ person }) => {
			if (!person || (!person.externalId && !person.auid)) return false;
			if (person.externalId && !allowedIds.includes(person.externalId)) return true;
			if (person.auid && !allowedIds.includes(person.auid)) return true;
			return false;
		})
		.map(({ id }) => id);
	if (ids.length) basket.removeItems(ids);
}
